import { useLayoutEffect } from "react";
import { useLocation } from "react-router-dom";
import { useRecoilState, useSetRecoilState } from "recoil";
import { parseQrContext } from "./qrContext";
import { cartState, cartOrderContextState } from "../recoil/atoms/cartAtom";
import { allProductsState, allCategoriesState, tableDataState } from "../recoil/atoms/dataAtom";
import { tableIdState } from "../recoil/atoms/tableAtom";
import { clearCache } from "../services/apiCacheHelper.ts";

const TABLE_ID_STORAGE_KEY = "tableId";

const RESERVED_ROUTES = new Set([
  "login",
  "cart",
  "search",
  "account",
  "order-view",
]);

export const parseTableId = (value) => parseQrContext(value)?.qrId || null;

export const getStoredTableId = () => {
  try {
    return parseTableId(
      window.sessionStorage.getItem(
        TABLE_ID_STORAGE_KEY
      )
    );
  } catch {
    return null;
  }
};

export const storeTableId = (value) => {
  const tableId = parseTableId(value);

  if (!tableId) {
    return null;
  }

  try {
    window.sessionStorage.setItem(
      TABLE_ID_STORAGE_KEY,
      tableId
    );

    window.sessionStorage.setItem("qrContext", JSON.stringify(parseQrContext(tableId)));
    return tableId;
  } catch {
    return null;
  }
};

export const getTableId = (
  pathname = window.location.pathname
) => {
  const pathSegment = pathname
    ? pathname.split("/").filter(Boolean)[0] || ""
    : "";

  const pathId = RESERVED_ROUTES.has(
    pathSegment.toLowerCase()
  )
    ? null
    : parseTableId(pathSegment);

  if (pathSegment && !RESERVED_ROUTES.has(pathSegment.toLowerCase())) return pathId;
  return getStoredTableId();
};

// Synchronize once above the routes, before mounting consumers of the new context.
export const useQrContextSync = () => {
  const { pathname } = useLocation();
  const [tableId, setTableId] = useRecoilState(tableIdState);
  const setCart = useSetRecoilState(cartState);
  const setOrderContext = useSetRecoilState(cartOrderContextState);
  const setProducts = useSetRecoilState(allProductsState);
  const setCategories = useSetRecoilState(allCategoriesState);
  const setDetails = useSetRecoilState(tableDataState);
  const segment = pathname.split("/").filter(Boolean)[0] || "";
  const isQrRoute = !!segment && !RESERVED_ROUTES.has(segment.toLowerCase());
  const pathId = isQrRoute ? parseTableId(segment) : null;
  const invalid = isQrRoute && !pathId;
  const desiredId = invalid ? null : pathId || tableId || getStoredTableId();
  useLayoutEffect(() => {
    if (desiredId !== tableId || invalid) {
      setCart([]);
      setOrderContext({});
      setProducts(null);
      setCategories(null);
      setDetails(null);
      clearTableData();
      try { window.sessionStorage.removeItem("authToken"); } catch {}
      setTableId(desiredId);
    }
    if (desiredId) storeTableId(desiredId);
  }, [desiredId, tableId, invalid, setCart, setOrderContext, setProducts, setCategories, setDetails, setTableId]);
  return { ready: desiredId === tableId, invalid };
};

// Compatibility name for existing consumers; the returned string may be T… or B….
export const useTableId = () => {
  const [tableId] = useRecoilState(tableIdState);
  return tableId;
};

export const clearTableData = () => {
  try {
    window.sessionStorage.removeItem(
      TABLE_ID_STORAGE_KEY
    );
  } catch {
    // Storage may be unavailable.
  }

  try { window.sessionStorage.removeItem("qrContext"); } catch {}
  clearCache();
};

export default useTableId;